import React from 'react';
import { Link } from 'react-router-dom';

const PlayersTable = ({ 
  players, 
  title = "Available Players",
  showCount = true,
  onPurchasePlayer,
  purchaseLoading,
  walletBalance,
  isTournamentStarted = false,
  showBuyButton = true,
  linkToBetting = false,
  tournament = null,
  user = null,
  onLoginRequired = null,
  pendingPlayerPurchase = null,
  setPendingPlayerPurchase = null
}) => {
  // Helper functions from LiveBetting
  const getTierPrice = (player) => {
    // Use backend-calculated price first if available
    if (player.price) {
      return player.price;
    }
    
    // Fallback to tier-based pricing if price not provided
    const tier = player.tier || player.golfer?.tier;
    const fallbackPrices = {
      'TIER_A': 250,
      'TIER_B': 200,
      'TIER_C': 150,
      'TIER_D': 100
    };
    return fallbackPrices[tier] || 100;
  };

  const getTierColor = (tier) => {
    const colors = {
      'TIER_A': 'tier-a',
      'TIER_B': 'tier-b',
      'TIER_C': 'tier-c',
      'TIER_D': 'tier-d',
      // Fallback for single letter tiers
      'A': 'tier-a',
      'B': 'tier-b',
      'C': 'tier-c',
      'D': 'tier-d'
    };
    return colors[tier] || 'tier-b';
  };

  const formatTierText = (tier) => {
    if (!tier) return 'Tier D';
    if (tier.startsWith('TIER_')) {
      return tier.replace('TIER_', 'Tier ');
    }
    return `Tier ${tier}`;
  };

  const handleBuyPlayer = (player, event) => {
    // Check if tournament is live
    if (tournament && tournament.status === 'LIVE') {
      console.log('Cannot buy players from a live tournament');
      return;
    }
    
    if (linkToBetting) {
      // Check if user is logged in
      if (!user) {
        // Prevent navigation
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        
        // Store player data for after login
        if (setPendingPlayerPurchase) {
          setPendingPlayerPurchase({
            golferId: player.golfer.id,
            player: player,
            tournamentId: tournament?.id
          });
        }
        
        // Trigger login modal
        if (onLoginRequired) {
          onLoginRequired();
        }
        return;
      }
      
      console.log(`Buying player: ${player.golfer.name} for $${getTierPrice(player)}`);
      // The Link component will handle navigation if logged in
    } else if (onPurchasePlayer) {
      onPurchasePlayer(player.golfer.id, player);
    }
  };

  return (
    <div className="available-players-section">
      <div className="available-players-header">
        <h2 className="available-players-title">
          {title}{showCount && ` (${players.length})`}
        </h2>
        {isTournamentStarted && (
          <div className="tournament-started-banner">
            <span className="banner-icon">⏰</span>
            <span className="banner-text">
              {tournament?.status === 'LIVE' 
                ? 'Tournament is LIVE - Player Selection is now closed' 
                : 'Tournament has started - The Player Selection Process is now closed'
              }
            </span>
          </div>
        )}
      </div>
      
      <div className="available-players-table-container">
        <div className="available-players-table-wrapper">
          <table className="available-players-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Tier</th>
                <th>Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {players.map((player) => (
                <tr key={player.golfer.id} className="player-row">
                  <td className="ranking-cell">
                    <span className="ranking-number">
                      {player.golfer.worldRanking || 'N/A'}
                    </span>
                  </td>
                  <td className="tier-cell">
                    <span className={`tier-badge ${getTierColor(player.tier || player.golfer.tier)}`}>
                      {formatTierText(player.tier || player.golfer.tier)}
                    </span>
                  </td>
                  <td className="name-cell">
                    {player.golfer.name}
                  </td>
                  <td className="action-cell">
                    {isTournamentStarted ? (
                      <div className="tournament-started-indicator">
                        <span className="started-text">
                          {tournament?.status === 'LIVE' ? 'LIVE' : 'STARTED'}
                        </span>
                      </div>
                    ) : showBuyButton ? (
                      // Check if player is available or purchased
                      player.isAvailable === false ? (
                        <button
                          className="taken-button"
                          disabled
                        >
                          TAKEN
                        </button>
                      ) : linkToBetting ? (
                        <Link 
                          to="/betting"
                          state={{
                            autoSelectPlayer: {
                              golferId: player.golfer.id,
                              player: player,
                              tournamentId: tournament?.id
                            }
                          }}
                          onClick={(e) => handleBuyPlayer(player, e)}
                        >
                          <button
                            className="proskins-btn proskins-btn-primary proskins-btn-small"
                            style={{ fontSize: '0.8em', padding: '4px 8px', whiteSpace: 'nowrap' }}
                          >
                            BUY ${getTierPrice(player)}
                          </button>
                        </Link>
                      ) : (
                        <button
                          onClick={() => handleBuyPlayer(player)}
                          disabled={purchaseLoading === player.golfer.id || walletBalance < getTierPrice(player)}
                          className={`buy-button ${walletBalance >= getTierPrice(player) ? 'enabled' : 'disabled'}`}
                        >
                          {purchaseLoading === player.golfer.id ? 'BUYING...' : `BUY $${getTierPrice(player)}`}
                        </button>
                      )
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {players.length === 0 && (
            <div className="empty-state no-players">
              No players available for purchase
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayersTable;
